import { useEffect, useRef, type ReactNode } from 'react';

/**
 * TTBottomSheet — the primary pattern for interrupting tasks in this app (docs/22 §3.1):
 * grabber 36×4, top corners 26px, padding 18px sides / 22px bottom.
 *
 * Built on <dialog> rather than a hand-rolled overlay so focus trapping, Esc and the
 * top-layer stacking come from the platform instead of from us. Two things the native
 * element does NOT give and are added here: closing on backdrop click, and respecting
 * the home-indicator inset when the sheet sits at the bottom of an installed PWA.
 */
export function BottomSheet({
  open,
  onClose,
  title,
  subtitle,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  /**
   * `<dialog>` fires `close` for BOTH reasons it can close: the user pressed Esc, and we called
   * `.close()` ourselves. Reporting the second one back to the parent as «the user closed this»
   * is how a sheet that replaces another sheet takes its replacement down with it — which is
   * exactly what happens when a delete confirmation escalates into the last-car guard.
   */
  const closingProgrammatically = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) {
      closingProgrammatically.current = true;
      el.close();
      closingProgrammatically.current = false;
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={() => {
        if (closingProgrammatically.current) return;
        onClose();
      }}
      onClick={(e) => {
        // A click lands on the dialog itself only when it hit the backdrop —
        // the content below stops propagation by occupying its own box.
        if (e.target === ref.current) onClose();
      }}
      aria-label={title}
      // Centred from the first pixel, not from a breakpoint: `sm:mx-auto` with a 560px cap left
      // the sheet pinned to the left edge in the 561–639px window.
      className="m-0 mx-auto mt-auto w-full max-w-[560px] rounded-t-[26px] bg-surface p-0 text-ink backdrop:bg-black/40"
      style={{ boxShadow: 'var(--shadow-sheet)' }}
    >
      <div className="px-[18px] pb-[22px] pt-[10px]">
        <div className="mx-auto mb-3.5 h-1 w-9 rounded-full bg-surface-alt-2" aria-hidden="true" />
        {/* `.sheet-title` is 25/29/800 — the same weight as a screen title, because a sheet
            interrupts the screen and has to read as its equal, not as a caption. */}
        {title && (
          <h2 className="text-[25px] font-extrabold leading-[1.16] tracking-[-0.01em]">{title}</h2>
        )}
        {subtitle && <p className="mt-1.5 mb-4 text-ink-soft">{subtitle}</p>}
        {!subtitle && title && <div className="h-4" aria-hidden="true" />}
        {children}
        <div style={{ height: 'env(safe-area-inset-bottom)' }} aria-hidden="true" />
      </div>
    </dialog>
  );
}
